"use client";

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from "react";
import styled, { css } from "styled-components";
import defaultTheme from "../defaultTheme";
import ErrorFormTooltip from "../ErrorFormTooltip";
import FormLabel from "../FormLabel";
import { SIZE_OPTIONS, RESIZE_OPTIONS } from "./consts";
import { rtlSpacing } from "../utils/rtl";
import getSpacingToken from "../common/getSpacingToken";
import useErrorTooltip from "../ErrorFormTooltip/hooks/useErrorTooltip";
import getFieldDataState from "../common/getFieldDataState";
import mq from "../utils/mediaQuery";
import useRandomId from "../hooks/useRandomId";
const Field = styled.label.withConfig({
  displayName: "Textarea__Field",
  componentId: "sc-1cskc7j-0"
})(["", ";"], ({
  theme,
  fullHeight
}) => css(["font-family:", ";display:flex;width:100%;position:relative;height:", ";flex-direction:column;flex:", ";margin-bottom:", ";"], theme.orbit.fontFamily, fullHeight && "100%", fullHeight && "1", getSpacingToken));
Field.defaultProps = {
  theme: defaultTheme
};
const getFontSize = ({
  theme,
  size
}) => {
  const tokens = {
    [SIZE_OPTIONS.SMALL]: theme.orbit.fontSizeInputSmall,
    [SIZE_OPTIONS.NORMAL]: theme.orbit.fontSizeInputNormal
  };
  if (!size) return null;
  return tokens[size];
};
const getLineHeight = ({
  theme,
  size
}) => {
  const tokens = {
    [SIZE_OPTIONS.SMALL]: theme.orbit.lineHeightTextSmall,
    [SIZE_OPTIONS.NORMAL]: theme.orbit.lineHeightTextNormal
  };
  if (!size) return null;
  return tokens[size];
};
const getPadding = ({
  theme,
  size
}) => {
  const tokens = {
    [SIZE_OPTIONS.SMALL]: theme.orbit.paddingTextareaSmall,
    [SIZE_OPTIONS.NORMAL]: theme.orbit.paddingTextareaNormal
  };
  if (!size) return null;
  return rtlSpacing(tokens[size]);
};

// TODO: add token for height
// flex is used for Stack
const StyledTextArea = styled.textarea.withConfig({
  displayName: "Textarea__StyledTextArea",
  componentId: "sc-1cskc7j-1"
})(["", ";"], ({
  theme,
  resize,
  fullHeight,
  error,
  disabled,
  readOnly
}) => css(["appearance:none;box-sizing:border-box;display:block;width:100%;height:", ";padding:", ";box-shadow:inset 0 0 0 ", " ", ";background-color:", ";color:", ";font-size:", ";line-height:", ";cursor:", ";font-family:", ";resize:", ";transition:box-shadow ", " ease-in-out;min-height:44px;border-radius:", ";", ";flex:", ";border:1px solid transparent;overflow:auto;&::placeholder{color:", ";}&:hover{box-shadow:", ";}"], fullHeight && "100%", getPadding, theme.orbit.borderWidthInput, error ? theme.orbit.borderColorInputError : theme.orbit.borderColorInput, disabled || readOnly ? theme.orbit.backgroundInputDisabled : theme.orbit.backgroundInput, disabled || readOnly ? theme.orbit.colorTextInputDisabled : theme.orbit.colorTextInput, getFontSize, getLineHeight, disabled ? "not-allowed" : "text", theme.orbit.fontFamily, resize, theme.orbit.durationFast, theme.orbit.borderRadiusNormal, mq.tablet(css(["border-radius:", ";"], theme.orbit.borderRadiusNormal)), fullHeight && "1", theme.orbit.colorPlaceholderInput, !(disabled || readOnly) && css(["inset 0 0 0 ", " ", ""], theme.orbit.borderWidthInput, error ? theme.orbit.borderColorInputErrorHover : theme.orbit.borderColorInputHover)));
StyledTextArea.defaultProps = {
  theme: defaultTheme
};
const Textarea = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    size = SIZE_OPTIONS.NORMAL,
    disabled,
    resize = RESIZE_OPTIONS.VERTICAL,
    dataTest,
    id,
    spaceAfter,
    fullHeight,
    defaultValue,
    value,
    label,
    name,
    error,
    placeholder,
    helpClosable = true,
    maxLength,
    onChange,
    onFocus,
    onBlur,
    tabIndex,
    help,
    rows,
    readOnly,
    required,
    dataAttrs
  } = props;
  const forID = useRandomId();
  const inputId = id || forID;
  const hasTooltip = Boolean(error || help);
  const {
    tooltipShown,
    tooltipShownHover,
    setTooltipShown,
    setTooltipShownHover,
    labelRef,
    iconRef,
    handleFocus
  } = useErrorTooltip({
    onFocus,
    hasTooltip
  });
  const shown = tooltipShown || tooltipShownHover;
  return /*#__PURE__*/React.createElement(Field, {
    fullHeight: fullHeight,
    spaceAfter: spaceAfter,
    ref: labelRef
  }, label && /*#__PURE__*/React.createElement(FormLabel, {
    filled: !!value,
    error: !!error,
    help: !!help,
    required: required,
    disabled: disabled,
    iconRef: iconRef,
    onMouseEnter: () => setTooltipShownHover(true),
    onMouseLeave: () => setTooltipShownHover(false)
  }, label), /*#__PURE__*/React.createElement(StyledTextArea, _extends({
    "data-state": getFieldDataState(!!error),
    "data-test": dataTest,
    "aria-required": !!required,
    id: inputId,
    name: name,
    value: value,
    defaultValue: defaultValue,
    size: size,
    fullHeight: fullHeight,
    disabled: disabled,
    error: error,
    placeholder: String(placeholder),
    maxLength: maxLength,
    onChange: onChange,
    rows: rows,
    onFocus: handleFocus,
    onBlur: onBlur,
    resize: resize,
    tabIndex: tabIndex ? Number(tabIndex) : undefined,
    readOnly: readOnly,
    ref: ref,
    "aria-describedby": shown ? `${inputId}-feedback` : undefined,
    "aria-invalid": error ? true : undefined
  }, dataAttrs)), hasTooltip && /*#__PURE__*/React.createElement(ErrorFormTooltip, {
    id: `${inputId}-feedback`,
    help: help,
    inputSize: size,
    helpClosable: helpClosable,
    error: error,
    onShown: setTooltipShown,
    shown: shown,
    referenceElement: labelRef
  }));
});
Textarea.displayName = "Textarea";
export default Textarea;